import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Eye, Plus, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { broadcastApi } from '../../../api';
import { templatesApi } from '../../../api/templates.api';
import { customersApi } from '../../../api/customers.api';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';
import MediaUpload from '../../../components/common/MediaUpload';
import TemplatePreview from '../../templates/components/TemplatePreview';

const BroadcastPage = ({
  data = [],
  broadcastNumbers = [],
  sess = { role: 'user', uid: '', mobile: '' },
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const { confirm } = useConfirm();
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  };
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });

  // Modal state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastName, setBroadcastName] = useState('');
  const [selectedBroadcastNumber, setSelectedBroadcastNumber] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showDateSection, setShowDateSection] = useState(false);
  const [dateTime, setDateTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Data for dropdowns
  const [templates, setTemplates] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [broadcastGroups, setBroadcastGroups] = useState([]);
  const [headerVariables, setHeaderVariables] = useState({});
  const [bodyVariables, setBodyVariables] = useState({});
  const [headerMediaHandle, setHeaderMediaHandle] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    broadcastName: '',
    recipients: '',
    template: '',
    dateTime: '',
    headerMedia: '',
    headerVars: {},
    bodyVars: {},
  });

  const normalizeTag = (tag) => {
    if (typeof tag === 'string') {
      return { id: tag, name: tag };
    }
    if (!tag || typeof tag !== 'object') {
      return { id: '', name: '' };
    }
    const id = tag.tag_id || tag.id || tag._id || tag.tagId || tag.tag || tag.name || '';
    const name = tag.tag_name || tag.name || tag.tag || `${id}`;
    return { id, name };
  };

  const normalizedTags = useMemo(() => {
    return tagOptions
      .map((tag) => normalizeTag(tag))
      .filter((tag) => `${tag.id}`.trim() && `${tag.name}`.trim());
  }, [tagOptions]);

  const tagNameById = useMemo(() => {
    return new Map(normalizedTags.map((tag) => [`${tag.id}`, tag.name]));
  }, [normalizedTags]);

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  const isAdmin = sess.role === 'admin' || sess.role === 'superadmin';

  const broadcastStats = useMemo(() => {
    const stats = {
      total: broadcasts.length,
      sent: 0,
      scheduled: 0,
      draft: 0,
      processing: 0,
      failed: 0,
      pending: 0,
    };
    broadcasts.forEach((item) => {
      const status = `${item.status || 'pending'}`.toLowerCase();
      if (status in stats) {
        stats[status] += 1;
      } else {
        stats.pending += 1;
      }
    });
    return stats;
  }, [broadcasts]);

  const paginatedBroadcasts = useMemo(() => {
    const start = page * rowsPerPage;
    return broadcasts.slice(start, start + rowsPerPage);
  }, [broadcasts, page, rowsPerPage]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date
      .toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
      .replace(/,/g, '');
  };

  useEffect(() => {
    const incoming =
      Array.isArray(data) && data.length > 0
        ? data
        : Array.isArray(data?.campaigns)
          ? data.campaigns
          : [];
    if (incoming.length > 0) {
      setBroadcasts(incoming);
    }
    setLoading(false);
  }, [data]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= broadcasts.length) {
      setPage(0);
    }
  }, [broadcasts.length, page, rowsPerPage]);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  useEffect(() => {
    loadBroadcasts();
    loadTemplates();
    loadTags();
    loadBroadcastGroups();
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!selectedTemplate) {
      setSelectedTemplateDetails(null);
      return undefined;
    }
    templatesApi.getById(selectedTemplate)
      .then((response) => {
        if (!isMounted) return;
        const details = response?.data?.data || response?.data || null;
        setSelectedTemplateDetails(details);
      })
      .catch(() => {
        if (isMounted) setSelectedTemplateDetails(null);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedTemplate]);

  const loadBroadcasts = async () => {
    try {
      setLoading(true);
      const response = await broadcastApi.getCampaigns();
      const payload = response?.data || {};
      const rawList =
        payload?.data?.campaigns ||
        payload?.campaigns ||
        payload?.data ||
        payload ||
        [];
      const list = Array.isArray(rawList) ? rawList : [];
      setBroadcasts(list);
      setStatus({ type: '', message: '' });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load broadcasts');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await templatesApi.getApproved({ page: 1, limit: 100 });
      const list =
        response?.data?.data ||
        response?.data?.templates ||
        response?.data?.results ||
        response?.data ||
        [];
      setTemplates(Array.isArray(list) ? list : []);
    } catch (error) {
      setTemplates([]);
    }
  };

  const loadTags = async () => {
    try {
      const response = await customersApi.getTags();
      const list =
        response?.data?.data ||
        response?.data?.tags ||
        response?.data ||
        [];
      if (Array.isArray(list)) {
        setTagOptions(list);
      } else if (Array.isArray(list?.items)) {
        setTagOptions(list.items);
      } else {
        setTagOptions([]);
      }
    } catch (error) {
      setTagOptions([]);
    }
  };

  const loadBroadcastGroups = async () => {
    try {
      const response = await broadcastApi.getGroups();
      const payload = response?.data || {};
      const rawList =
        payload?.data?.groups ||
        payload?.groups ||
        payload?.data ||
        payload ||
        [];
      setBroadcastGroups(Array.isArray(rawList) ? rawList : []);
    } catch (error) {
      setBroadcastGroups([]);
    }
  };

  const handleBroadcastDelete = async (id, name) => {
    const isConfirmed = await confirm({
      title: 'Delete Broadcast',
      message: `Are you sure you want to delete "${name}"?`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    try {
      await broadcastApi.deleteCampaign(id);
      setBroadcasts((prev) => prev.filter((item) => (item.id || item.campaignId) !== id));
      setStatus({ type: 'success', message: 'Broadcast deleted successfully' });
    } catch (error) {
      const message = error?.response?.data?.message || 'Error deleting broadcast';
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
  };

  const getStatusColor = (s) => {
    const colors = {
      pending: 'warning',
      scheduled: 'info',
      processing: 'primary',
      sent: 'success',
      completed: 'success',
      failed: 'error',
      cancelled: 'default',
    };
    return colors[s] || 'default';
  };

  const openBroadcastModal = () => {
    setShowBroadcastModal(true);
  };

  const closeBroadcastModal = () => {
    setShowBroadcastModal(false);
    setBroadcastName('');
    setSelectedBroadcastNumber('');
    setSelectedTag('');
    setSelectedTemplate('');
    setShowDateSection(false);
    setDateTime('');
    setHeaderVariables({});
    setBodyVariables({});
    setHeaderMediaHandle('');
    setSelectedTemplateDetails(null);
    setValidationErrors({
      broadcastName: '',
      recipients: '',
      template: '',
      dateTime: '',
      headerMedia: '',
      headerVars: {},
      bodyVars: {},
    });
  };

  const getTemplateValue = (template) =>
    template?.id || template?.t_id || template?.templateId || '';
  const getTemplateChannel = (template) => {
    const channel = template?.channel || template?.t_channel || template?.platform;
    return channel ? `${channel}`.toLowerCase() : 'whatsapp';
  };

  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1]);
    }
    const unique = [...new Set(matches)];
    const allNumeric = unique.every((value) => /^\d+$/.test(`${value}`));
    if (allNumeric) {
      return unique.sort((a, b) => Number(a) - Number(b));
    }
    return unique;
  };

  const getTemplateHeader = (template) => {
    if (!template) return null;
    const headerComponent = selectedTemplateDetails?.components?.find(
      (comp) => String(comp?.type || '').toLowerCase() === 'header'
    );
    if (headerComponent) {
      const format = headerComponent.format || headerComponent.content?.format || '';
      const text = headerComponent.content?.text || '';
      return { type: format ? `${format}`.toLowerCase() : '', text };
    }
    const headerType =
      template.header?.type ||
      template.header_type ||
      template.t_htype ||
      template.headerType ||
      template.headerFormat ||
      template.header_format ||
      template.t_header_type ||
      '';
    const headerText =
      template.header?.text ||
      template.header_text ||
      template.t_htext ||
      template.headerText ||
      template.header_content ||
      template.t_header_text ||
      '';
    return { type: headerType ? `${headerType}`.toLowerCase() : '', text: headerText };
  };

  const getTemplateBody = (template) => {
    const bodyComponent = selectedTemplateDetails?.components?.find(
      (comp) => String(comp?.type || '').toLowerCase() === 'body'
    );
    if (bodyComponent) {
      return bodyComponent.content?.text || '';
    }
    const bodyValue = template?.body;
    if (typeof bodyValue === 'string') return bodyValue;
    return (
      bodyValue?.text ||
      template?.content ||
      template?.t_btext ||
      template?.message ||
      ''
    );
  };

  const selectedTemplateData = useMemo(() => {
    if (!selectedTemplate) return null;
    return templates.find(
      (t) => `${getTemplateValue(t)}` === `${selectedTemplate}`
    );
  }, [selectedTemplate, templates]);

  const templateHeaderInfo = useMemo(() => {
    return getTemplateHeader(selectedTemplateData);
  }, [selectedTemplateData]);

  const templateHeaderVariables = useMemo(() => {
    if (!templateHeaderInfo?.text) return [];
    return extractVariables(templateHeaderInfo.text);
  }, [templateHeaderInfo]);

  const templateBodyVariables = useMemo(() => {
    const bodyText = getTemplateBody(selectedTemplateData);
    return extractVariables(bodyText);
  }, [selectedTemplateData]);

  const templateHasMediaHeader = useMemo(() => {
    if (!templateHeaderInfo?.type) return false;
    const mediaTypes = ['image', 'video', 'document'];
    return mediaTypes.includes(templateHeaderInfo.type.toLowerCase());
  }, [templateHeaderInfo]);

  const headerMediaCategory = useMemo(() => {
    if (!templateHeaderInfo?.type) return 'all';
    const type = templateHeaderInfo.type.toLowerCase();
    if (type === 'image') return 'image';
    if (type === 'video') return 'video';
    if (type === 'document') return 'document';
    return 'all';
  }, [templateHeaderInfo]);

  const templatePreviewData = useMemo(() => {
    if (!selectedTemplateData && !selectedTemplateDetails) return null;
    const components =
      selectedTemplateDetails?.components ||
      selectedTemplateDetails?.templateComponents ||
      selectedTemplateDetails?.preview?.components ||
      selectedTemplateDetails?.message_template_components ||
      [];
    const footerComponent = components.find(
      (comp) => String(comp?.type || comp?.component_type || '').toLowerCase() === 'footer'
    );
    const buttonsComponent = components.find(
      (comp) => String(comp?.type || comp?.component_type || '').toLowerCase() === 'buttons'
    );
    const footerText =
      footerComponent?.text ||
      footerComponent?.content?.text ||
      selectedTemplateData?.footer?.text ||
      selectedTemplateData?.footer ||
      '';
    const buttons =
      buttonsComponent?.buttons ||
      buttonsComponent?.content?.buttons ||
      selectedTemplateData?.buttons ||
      [];
    const variableSamples = { header: {}, body: {} };
    templateHeaderVariables.forEach((key) => {
      const value = headerVariables[key];
      if (value) {
        variableSamples.header[`${key}`.toLowerCase()] = value;
      }
    });
    templateBodyVariables.forEach((key) => {
      const value = bodyVariables[key];
      if (value) {
        variableSamples.body[`${key}`.toLowerCase()] = value;
      }
    });

    return {
      headerType: templateHeaderInfo?.type || 'none',
      header: templateHeaderInfo?.text || '',
      body: getTemplateBody(selectedTemplateData),
      footer: footerText,
      buttons: Array.isArray(buttons) ? buttons : [],
      variableSamples,
    };
  }, [
    selectedTemplateData,
    selectedTemplateDetails,
    templateHeaderInfo,
    templateHeaderVariables,
    templateBodyVariables,
    headerVariables,
    bodyVariables,
  ]);

  const buildComponentsPayload = () => {
    const components = [];

    if (templateHeaderVariables.length > 0) {
      const headerParams = templateHeaderVariables.map((varIndex) => ({
        type: 'text',
        text: headerVariables[varIndex] || '',
      }));
      if (headerParams.length > 0) {
        components.push({
          type: 'header',
          parameters: headerParams,
        });
      }
    }

    const bodyParams = templateBodyVariables.map((varIndex) => ({
      type: 'text',
      text: bodyVariables[varIndex] || '',
    }));
    components.push({
      type: 'body',
      parameters: bodyParams,
    });

    return components;
  };

  const handleHeaderVariableChange = (varIndex, value) => {
    setHeaderVariables((prev) => ({
      ...prev,
      [varIndex]: value,
    }));
  };

  const handleBodyVariableChange = (varIndex, value) => {
    setBodyVariables((prev) => ({
      ...prev,
      [varIndex]: value,
    }));
  };

  const handleHeaderMediaUploadComplete = (result) => {
    setHeaderMediaHandle(result.handle);
  };

  const handleCreateBroadcast = async () => {
    const nextErrors = {
      broadcastName: '',
      recipients: '',
      template: '',
      dateTime: '',
      headerMedia: '',
      headerVars: {},
      bodyVars: {},
    };

    if (!broadcastName.trim()) {
      nextErrors.broadcastName = 'Broadcast name is required.';
    }

    if (!selectedBroadcastNumber && !selectedTag) {
      nextErrors.recipients = 'Select a broadcast number or a tag.';
    }

    if (!selectedTemplate) {
      nextErrors.template = 'Template is required.';
    }

    if (showDateSection && !dateTime) {
      nextErrors.dateTime = 'Schedule date and time is required.';
    }

    if (selectedTemplate) {
      for (const varIndex of templateHeaderVariables) {
        if (!headerVariables[varIndex]?.trim()) {
          nextErrors.headerVars[varIndex] = `Header variable {{${varIndex}}} is required.`;
        }
      }

      for (const varIndex of templateBodyVariables) {
        if (!bodyVariables[varIndex]?.trim()) {
          nextErrors.bodyVars[varIndex] = `Body variable {{${varIndex}}} is required.`;
        }
      }

      if (templateHasMediaHeader && !headerMediaHandle) {
        nextErrors.headerMedia = `Upload ${headerMediaCategory} for the template header.`;
      }
      if (!templateHasMediaHeader && headerMediaHandle) {
        nextErrors.headerMedia = 'Media is not allowed for this template.';
      }
    }

    if (
      nextErrors.broadcastName ||
      nextErrors.recipients ||
      nextErrors.template ||
      nextErrors.dateTime ||
      nextErrors.headerMedia ||
      Object.keys(nextErrors.headerVars).length > 0 ||
      Object.keys(nextErrors.bodyVars).length > 0
    ) {
      setValidationErrors(nextErrors);
      return;
    }

    setValidationErrors({
      broadcastName: '',
      recipients: '',
      template: '',
      dateTime: '',
      headerMedia: '',
      headerVars: {},
      bodyVars: {},
    });
    setSubmitting(true);

    try {
      const components = buildComponentsPayload();
      const hasHeaderMedia = templateHasMediaHeader && Boolean(headerMediaHandle);
      const tagId = selectedTag ? Number(selectedTag) : null;
      const normalizedTagId = Number.isNaN(tagId) ? selectedTag : tagId;
      const payload = {
        campaign_name: broadcastName.trim(),
        send_type: selectedBroadcastNumber ? 'group' : 'tag',
        bgid: selectedBroadcastNumber || undefined,
        tag_id: normalizedTagId || undefined,
        template_id: selectedTemplate,
        channel: getTemplateChannel(selectedTemplateData),
        has_media: hasHeaderMedia,
        media_type: hasHeaderMedia ? headerMediaCategory : null,
        media_id: hasHeaderMedia ? headerMediaHandle : null,
        payload: components.length > 0 ? { components } : {},
        user_id: sess.uid,
      };

      const response = await broadcastApi.create(payload);
      const created = response?.data?.data || response?.data || {};
      const createdId = created.id || created.campaignId;

      if (showDateSection && createdId) {
        await broadcastApi.schedule({ campaignId: createdId, scheduledAt: dateTime });
        setStatus({ type: 'success', message: 'Broadcast scheduled successfully!' });
      } else if (createdId) {
        await broadcastApi.send({ campaignId: createdId });
        setStatus({ type: 'success', message: 'Broadcast sent successfully!' });
      }

      closeBroadcastModal();
      loadBroadcasts();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to create broadcast');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Use broadcastNumbers prop or loaded groups
  const numberOptions = broadcastNumbers.length > 0 ? broadcastNumbers : broadcastGroups;
  const showBroadcastSelect = !selectedTag;
  const showTagsSelect = !selectedBroadcastNumber;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
        minHeight: '100%',
      }}
    >
      <Card
        sx={{
          mb: 2,
          borderRadius: 3,
          color: theme.palette.text.primary,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          boxShadow: `0 18px 45px ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Broadcast Campaigns
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(30, 41, 59, 0.7)' }}>
                Track campaign performance, schedule sends, and manage recipients at a glance.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={openBroadcastModal}
                sx={{
                  px: 2.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                  boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                Create Broadcast
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/broadcast/schedule')}
                sx={{
                  color: theme.palette.text.primary,
                  borderColor: alpha(theme.palette.primary.main, 0.25),
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                Scheduled Broadcasts
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3} flexWrap="wrap">
            {[
              { label: 'Total Campaigns', value: broadcastStats.total },
              { label: 'Sent', value: broadcastStats.sent },
              { label: 'Scheduled', value: broadcastStats.scheduled },
              { label: 'Drafts', value: broadcastStats.draft },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: 'rgba(255, 255, 255, 0.7)',
                  minWidth: 140,
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(71, 85, 105, 0.75)' }}>
                  {stat.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {stat.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" mb={2}>
        <Button
          variant="outlined"
          onClick={() => navigate('/broadcast/groups')}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: 'rgba(148, 163, 184, 0.5)',
            color: '#0f172a',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              borderColor: 'rgba(100, 116, 139, 0.7)',
              backgroundColor: 'rgba(15, 23, 42, 0.05)',
            },
          }}
        >
          Broadcast Groups
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/broadcast/history')}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: 'rgba(148, 163, 184, 0.5)',
            color: '#0f172a',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              borderColor: 'rgba(100, 116, 139, 0.7)',
              backgroundColor: 'rgba(15, 23, 42, 0.05)',
            },
          }}
        >
          Broadcast History
        </Button>
      </Stack>

      {status.message && status.type === 'error' && (
        <Typography
          color="error"
          variant="body2"
          sx={{ mb: 2 }}
        >
          {status.message}
        </Typography>
      )}

      <Card
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(148, 163, 184, 0.12)' }}>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Name
                    </TableCell>
                    {isAdmin && (
                      <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Created By
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Recipients
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Created
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBroadcasts.map((item, i) => {
                    const id = item.id || item.campaignId || item._id;
                    const name = item.name || item.campaignName || 'Broadcast';
                    const createdBy = item.createdByName || item.ownerName || '-';
                    const recipientCount =
                      item.stats?.totalRecipients ??
                      item.stats?.total_recipients ??
                      item.stats?.recipients ??
                      item.stats?.total ??
                      item.recipientCount ??
                      item.recipients_count ??
                      item.total_recipients ??
                      (Array.isArray(item.recipients) ? item.recipients.length : 0);
                    const itemStatus = item.status || 'pending';
                    const createdAt = item.createdAt || item.created_at;

                    return (
                      <TableRow
                        key={id || i}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.06),
                          },
                        }}
                      >
                        <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                        {isAdmin && <TableCell sx={{ color: 'text.secondary' }}>{createdBy}</TableCell>}
                        <TableCell sx={{ fontWeight: 600 }}>{recipientCount}</TableCell>
                        <TableCell>
                          <Chip
                            label={itemStatus.charAt(0).toUpperCase() + itemStatus.slice(1)}
                            color={getStatusColor(itemStatus)}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1.5, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(createdAt)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="View broadcast"
                              onClick={() => navigate(`/broadcast/detail/${id}`)}
                              sx={{
                                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.15)' },
                              }}
                            >
                              <Eye size={16} />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Delete broadcast"
                              onClick={() => handleBroadcastDelete(id, name)}
                              sx={{
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.15)' },
                              }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {broadcasts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6}>
                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                          No broadcast campaigns found. Click "Create Broadcast" to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
        <TablePagination
          component="div"
          count={broadcasts.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </Card>

      {/* Create Broadcast Modal */}
      <Dialog
        open={showBroadcastModal}
        onClose={closeBroadcastModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Create Broadcast
          <IconButton onClick={closeBroadcastModal} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Broadcast Name:
              </Typography>
              <TextField
                value={broadcastName}
                onChange={(e) => setBroadcastName(e.target.value)}
                placeholder="Enter broadcast campaign name"
                size="small"
                error={Boolean(validationErrors.broadcastName)}
                helperText={validationErrors.broadcastName}
                fullWidth
              />
            </Box>

            {/* Broadcast Number */}
            {showBroadcastSelect && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Broadcast Number:
                </Typography>
                <FormControl fullWidth size="small" error={Boolean(validationErrors.recipients)}>
                  <Select
                    value={selectedBroadcastNumber}
                    onChange={(e) => {
                      setSelectedBroadcastNumber(e.target.value);
                      if (e.target.value) {
                        setSelectedTag('');
                      }
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>-- Select Option --</em>
                    </MenuItem>
                    {numberOptions.map((item) => {
                      const id = item.id || item.bn_id || item.groupId;
                      const name = item.name || item.bn_name || item.groupName || 'Group';
                      return (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {validationErrors.recipients && (
                    <FormHelperText>{validationErrors.recipients}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}

            {showBroadcastSelect && showTagsSelect && (
              <Typography align="center" color="text.secondary">
                or
              </Typography>
            )}

            {/* Tags */}
            {showTagsSelect && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Tags:
                </Typography>
                <FormControl fullWidth size="small" error={Boolean(validationErrors.recipients)}>
                  <Select
                    value={selectedTag}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedTag(value || '');
                      if (value) {
                        setSelectedBroadcastNumber('');
                      }
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>-- Select Tag --</em>
                    </MenuItem>
                    {normalizedTags.map((tag, idx) => {
                      const tagName = tag.name;
                      const tagId = tag.id;
                      return (
                        <MenuItem key={`${tagId}-${idx}`} value={tagId}>
                          {tagName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {validationErrors.recipients && (
                    <FormHelperText>{validationErrors.recipients}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}

            {/* Template */}
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Template:
              </Typography>
              <FormControl fullWidth size="small" error={Boolean(validationErrors.template)}>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    setHeaderVariables({});
                    setBodyVariables({});
                    setHeaderMediaHandle('');
                    setSelectedTemplateDetails(null);
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select any one</em>
                  </MenuItem>
                  {templates.map((item) => {
                    const id = getTemplateValue(item);
                    const name = item.name || item.t_tname || item.title || 'Template';
                    return (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {validationErrors.template && (
                  <FormHelperText>{validationErrors.template}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Template Variables */}
            {selectedTemplate && (templateHasMediaHeader || templateHeaderVariables.length > 0 || templateBodyVariables.length > 0) && (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Template Variables
                </Typography>

                {templateHasMediaHeader && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Header {headerMediaCategory.charAt(0).toUpperCase() + headerMediaCategory.slice(1)} *
                    </Typography>
                    <MediaUpload
                      category={headerMediaCategory}
                      onUploadComplete={handleHeaderMediaUploadComplete}
                      onError={(err) => setStatus({ type: 'error', message: err })}
                      compact
                    />
                    {validationErrors.headerMedia && (
                      <Typography variant="caption" color="error">
                        {validationErrors.headerMedia}
                      </Typography>
                    )}
                  </Box>
                )}

                {templateHeaderVariables.length > 0 && !templateHasMediaHeader && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Header Variables
                    </Typography>
                    <Stack spacing={1.5}>
                      {templateHeaderVariables.map((varIndex) => (
                        <TextField
                          key={`header-${varIndex}`}
                          label={`Header Variable {{${varIndex}}}`}
                          value={headerVariables[varIndex] || ''}
                          onChange={(e) => handleHeaderVariableChange(varIndex, e.target.value)}
                          placeholder={`Enter value for {{${varIndex}}}`}
                          size="small"
                          required
                          error={Boolean(validationErrors.headerVars[varIndex])}
                          helperText={validationErrors.headerVars[varIndex]}
                          fullWidth
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {templateBodyVariables.length > 0 && (
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Body Variables
                    </Typography>
                    <Stack spacing={1.5}>
                      {templateBodyVariables.map((varIndex) => (
                        <TextField
                          key={`body-${varIndex}`}
                          label={`Body Variable {{${varIndex}}}`}
                          value={bodyVariables[varIndex] || ''}
                          onChange={(e) => handleBodyVariableChange(varIndex, e.target.value)}
                          placeholder={`Enter value for {{${varIndex}}}`}
                          size="small"
                          required
                          error={Boolean(validationErrors.bodyVars[varIndex])}
                          helperText={validationErrors.bodyVars[varIndex]}
                          fullWidth
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {selectedTemplate && templatePreviewData && (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Template Preview
                </Typography>
                <TemplatePreview template={templatePreviewData} />
              </Box>
            )}

            {/* Schedule Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={showDateSection}
                  onChange={(e) => setShowDateSection(e.target.checked)}
                  color="primary"
                />
              }
              label="Schedule Date and Time"
            />

            {/* Date Time Picker */}
            {showDateSection && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Date and Time for Sending the Broadcast:
                </Typography>
                <TextField
                  type="datetime-local"
                  value={dateTime}
                  onChange={(e) => setDateTime(e.target.value)}
                  fullWidth
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: new Date().toISOString().slice(0, 16) },
                  }}
                  error={Boolean(validationErrors.dateTime)}
                  helperText={validationErrors.dateTime}
                />
              </Box>
            )}

            {/* Submit Button */}
            <Button
              variant="contained"
              color="success"
              onClick={handleCreateBroadcast}
              disabled={submitting}
              sx={{ mt: 1 }}
            >
              {submitting
                ? 'Processing...'
                : showDateSection
                  ? 'Schedule Broadcast'
                  : 'Send Broadcast'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BroadcastPage;
